import { jsPDF } from "jspdf";
import { Expansion, Product } from "types";
import { getNumberInGermanCurrency } from "utils/currency";

export type ExportLanguage = "de" | "en";

export type GenerateExpansionCardListPdfParams = {
  /** Karten, die ins PDF sollen (bereits auf aktive Karten gefiltert). */
  products: Product[];
  expansion: Expansion;
  language: ExportLanguage;
};

// Layout-Konstanten (pt) – A4 Hochformat. Tuning-Knöpfe; alles Weitere wird abgeleitet.
const MARGIN_X = 36;
const MARGIN_TOP = 24;
const MARGIN_BOTTOM = 24;
const FOOTER_H = 14;

const TITLE_FONT = 13;
const TITLE_LINE_GAP = 7; // Abstand Titel-Grundlinie → Trennlinie
const LINE_CONTENT_GAP = 5; // Abstand Trennlinie → erste Kartenzeile

const GUTTER = 12;
const PRICE_GAP = 6;

type RowLayout = {
  /** Spaltenanzahl. Der Kunde hat 2 bis 4 vorgegeben. */
  columns: number;
  fontSize: number;
  rowHeight: number;
};

/**
 * Layout-Stufen von luftig nach dicht.
 *
 * Ausgewählt wird die ERSTE Stufe, auf der die Edition noch auf eine Seite passt -
 * also die größte Schrift und die wenigsten Spalten, die reichen. Absichtlich in
 * dieser Richtung: eine kleine Edition soll nicht in 7 pt und drei Spalten auf einer
 * dreiviertel leeren Seite stehen.
 *
 * Wenige Spalten sind zusätzlich lesbarer, weil breitere Spalten weniger
 * Kartennamen abschneiden - bei 4 Spalten bleiben nach Abzug des Preises nur noch
 * rund 26 Zeichen für den Namen.
 *
 * Kapazität pro Seite bei A4 hoch (754,9 pt nutzbare Inhaltshöhe), verifiziert:
 *   2 Sp. / 12,0 pt -> 124 Karten      3 Sp. /  9,5 pt -> 237 Karten
 *   2 Sp. / 10,5 pt -> 142 Karten      4 Sp. /  9,5 pt -> 316 Karten
 *   3 Sp. / 10,5 pt -> 213 Karten      4 Sp. /  8,0 pt -> 376 Karten
 *
 * 6 pt ist bewusst die Untergrenze. Eine Stufe darunter (4 Sp. / 7,5 pt) würde
 * 400 Karten auf eine Seite bringen, also nur den schmalen Bereich 377-400
 * gewinnen - und das mit einer Schrift, die auf Papier kaum lesbar ist. Der Kunde
 * rechnet für die wenigen großen Editionen ohnehin mit zwei Seiten.
 */
const ROW_LAYOUTS: RowLayout[] = [
  { columns: 2, fontSize: 9, rowHeight: 12 },
  { columns: 2, fontSize: 8, rowHeight: 10.5 },
  { columns: 3, fontSize: 8, rowHeight: 10.5 },
  { columns: 3, fontSize: 7, rowHeight: 9.5 },
  { columns: 4, fontSize: 7, rowHeight: 9.5 },
  { columns: 4, fontSize: 6, rowHeight: 8 },
];

/**
 * Wählt die luftigste Stufe, die alle Karten auf eine Seite bringt.
 *
 * Passt selbst die dichteste Stufe nicht, wird sie trotzdem genommen und das PDF
 * läuft auf mehrere Seiten - das betrifft nur die wenigen sehr großen Editionen.
 * Die Alternative wäre eine Schriftgröße, die niemand mehr lesen kann.
 */
const selectRowLayout = (
  cardCount: number,
  contentHeight: number,
): { layout: RowLayout; rowsPerColumn: number } => {
  const getRowsPerColumn = (rowHeight: number) =>
    Math.max(1, Math.floor(contentHeight / rowHeight));

  const fitting = ROW_LAYOUTS.find(
    (layout) =>
      getRowsPerColumn(layout.rowHeight) * layout.columns >= cardCount,
  );

  const layout = fitting ?? ROW_LAYOUTS[ROW_LAYOUTS.length - 1];

  return { layout, rowsPerColumn: getRowsPerColumn(layout.rowHeight) };
};

/**
 * Zeilenanzahl je Spalte einer Seite - die Karten laufen spaltenweise von links
 * oben nach rechts unten, nicht zeilenweise.
 *
 * Die Karten einer Seite werden gleichmäßig auf die Spalten verteilt, statt
 * Spalte für Spalte bis zur Seitenunterkante zu füllen. Sonst stünden auf einer
 * nicht ganz gefüllten Seite zwei randvolle Spalten neben einer mit drei
 * Einträgen - die Layout-Stufe wird ja nach Kapazität und nicht nach exakter
 * Kartenzahl gewählt. Die ersten Spalten bekommen die Restkarten, dadurch sind
 * sie höchstens eine Zeile länger als die letzten.
 */
const getRowsPerColumnOnPage = (
  cardsOnPage: number,
  columns: number,
): number[] => {
  const base = Math.floor(cardsOnPage / columns);
  const remainder = cardsOnPage % columns;

  return Array.from(
    { length: columns },
    (_, col) => base + (col < remainder ? 1 : 0),
  );
};

const FONT_FAMILY = "helvetica";
const ELLIPSIS = "…";
const NO_PRICE = "—";

type CardEntry = {
  name: string;
  price: string;
};

const sanitizeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]/g, "_").trim() || "Kartenliste";

const getExpansionTitle = (
  expansion: Expansion,
  language: ExportLanguage,
): string => {
  if (language === "de") {
    return (
      expansion.expansionAlias ??
      expansion.expansionDeName ??
      expansion.expansionEnName ??
      ""
    );
  }

  return expansion.expansionEnName ?? expansion.expansionDeName ?? "";
};

const getCardName = (product: Product, language: ExportLanguage): string => {
  if (language === "de") {
    return product.productAlias ?? product.productDeName ?? "";
  }

  return product.productEnName ?? product.productDeName ?? "";
};

/**
 * Kürzt einen Text auf die verfügbare Breite und hängt bei Bedarf "…" an.
 * Sucht den längsten passenden Präfix per Binärsuche über die Textbreite.
 * Erwartet, dass Font und Schriftgröße vorher gesetzt wurden.
 */
const ellipsize = (doc: jsPDF, text: string, maxWidth: number): string => {
  if (maxWidth <= 0) return "";
  if (doc.getTextWidth(text) <= maxWidth) return text;

  const ellipsisWidth = doc.getTextWidth(ELLIPSIS);

  let low = 0;
  let high = text.length;

  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (doc.getTextWidth(text.slice(0, mid)) + ellipsisWidth <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return low === 0 ? ELLIPSIS : text.slice(0, low).trimEnd() + ELLIPSIS;
};

export const generateExpansionCardListPdf = ({
  products,
  expansion,
  language,
}: GenerateExpansionCardListPdfParams): void => {
  const title = getExpansionTitle(expansion, language);

  const cards: CardEntry[] = products
    .map((product) => ({
      name: getCardName(product, language),
      price:
        product.priceStore == null
          ? NO_PRICE
          : getNumberInGermanCurrency(product.priceStore),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, language));

  const doc = new jsPDF("p", "pt", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const titleBaseline = MARGIN_TOP + TITLE_FONT;
  const titleLineY = titleBaseline + TITLE_LINE_GAP;
  const contentTop = titleLineY + LINE_CONTENT_GAP;
  const bottomLimit = pageHeight - MARGIN_BOTTOM - FOOTER_H;

  // Die Inhaltshöhe hängt nur an Rändern, Titel und Fußzeile - nicht an der
  // Zeilenschrift. Die Kapazität einer Stufe ist damit rein rowHeight x columns.
  const { layout, rowsPerColumn } = selectRowLayout(
    cards.length,
    bottomLimit - contentTop,
  );

  const usableWidth = pageWidth - 2 * MARGIN_X;
  const columnWidth =
    (usableWidth - (layout.columns - 1) * GUTTER) / layout.columns;
  const colX = (col: number) => MARGIN_X + col * (columnWidth + GUTTER);

  const cardsPerPage = rowsPerColumn * layout.columns;
  const totalPages = Math.max(1, Math.ceil(cards.length / cardsPerPage));

  console.log(
    `Kartenlisten-PDF "${title}": ${cards.length} Karten, ${layout.columns} Spalten, ${layout.fontSize} pt, ${rowsPerColumn} Zeilen/Spalte, ${totalPages} Seite(n).`,
  );

  const drawHeader = () => {
    doc.setFont(FONT_FAMILY, "bold");
    doc.setFontSize(TITLE_FONT);
    doc.setTextColor(0, 0, 0);
    doc.text(title, MARGIN_X, titleBaseline);

    doc.setLineWidth(0.5);
    doc.setDrawColor(180, 180, 180);
    doc.line(MARGIN_X, titleLineY, pageWidth - MARGIN_X, titleLineY);
  };

  const drawFooter = (pageIndex: number) => {
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Seite ${pageIndex + 1} / ${totalPages}`,
      pageWidth / 2,
      pageHeight - MARGIN_BOTTOM / 2,
      { align: "center" },
    );
    doc.setTextColor(0, 0, 0);
  };

  const drawCard = (card: CardEntry, col: number, row: number) => {
    const xLeft = colX(col);
    const xRight = xLeft + columnWidth;
    // text()-y ist die Baseline, daher die Schriftgröße auf die Zeilenoberkante
    // addieren.
    const yBaseline = contentTop + row * layout.rowHeight + layout.fontSize;

    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(layout.fontSize);
    doc.setTextColor(0, 0, 0);

    const { name, price } = card;
    const priceWidth = doc.getTextWidth(price);
    const nameMaxWidth = columnWidth - priceWidth - PRICE_GAP;
    const nameToDraw = ellipsize(doc, name, nameMaxWidth);

    doc.text(nameToDraw, xLeft, yBaseline);
    doc.text(price, xRight, yBaseline, { align: "right" });
  };

  for (let page = 0; page < totalPages; page++) {
    if (page > 0) doc.addPage();
    drawHeader();

    const pageStart = page * cardsPerPage;
    const pageEnd = Math.min(pageStart + cardsPerPage, cards.length);
    const rowCounts = getRowsPerColumnOnPage(
      pageEnd - pageStart,
      layout.columns,
    );

    let index = pageStart;

    for (let col = 0; col < layout.columns; col++) {
      for (let row = 0; row < rowCounts[col]; row++) {
        drawCard(cards[index], col, row);
        index++;
      }
    }

    drawFooter(page);
  }

  const fileName = `${sanitizeFileName(title)}_${language.toUpperCase()}.pdf`;
  doc.save(fileName);
};
